import React, { useState } from 'react';
import { Container, Grid, Paper, Typography, Button, Box, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { getUser } from '../../services/auth';
import { UserUtils } from '../../types';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QuizIcon from '@mui/icons-material/Quiz';
import InsightsIcon from '@mui/icons-material/Insights';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';

const TILE_HEIGHT = 200;
const TILE_MARGIN = 3;

const HomeTile = ({
    title,
    description,
    onClick,
    icon
}: {
    title: string;
    description: string;
    onClick: () => void;
    icon: React.ReactNode;
}) => {
    return (
        <Grid item xs={12} sm={6} md={4} lg={3}>
            <Paper
                elevation={1}
                sx={{
                    p: 3,
                    height: TILE_HEIGHT,
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.15)',
                        borderColor: 'primary.main',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                        '& .tile-icon': {
                            transform: 'scale(1.1)',
                            color: 'primary.main',
                        },
                        '& .tile-button': {
                            transform: 'translateX(4px)',
                        }
                    }
                }}
                onClick={onClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box 
                        className="tile-icon"
                        sx={{ 
                            color: 'primary.main', 
                            fontSize: 28,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            mr: 1.5
                        }}
                    >
                        {icon}
                    </Box>
                    <Tooltip title={title} arrow placement="top">
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.3,
                                fontSize: '1.1rem'
                            }}
                        >
                            {title}
                        </Typography>
                    </Tooltip>
                </Box>

                <Tooltip title={description} arrow placement="top">
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.5,
                            mb: 3,
                            flex: 1
                        }}
                    >
                        {description}
                    </Typography>
                </Tooltip>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Button
                        className="tile-button"
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ 
                            px: 3,
                            py: 1,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        Open
                    </Button>
                </Box>
            </Paper>
        </Grid>
    );
};

const Home = () => {
    const navigate = useNavigate();
    const user = getUser();

    const allTiles = {
        userManagement: {
            title: 'User Management',
            description: 'Manage users, roles and permissions',
            path: '/user-management',
            icon: <PeopleIcon />
        },
        reviewEntityManagement: {
            title: 'Review Entity Management',
            description: 'Add and manage review entities such as projects, programs and their attributes',
            path: '/review-entities',
            icon: <AssessmentIcon />
        },
        // Removed manageMetrics tile
        organizationSetup: {
            title: 'Organization defaults',
            description: 'Set-up organization defaults',
            path: '/organization-setup',
            icon: <BusinessIcon />
        },
        reviewManagement: {
            title: 'Review Management',
            description: 'Manage active reviews, closed reviews, and create new reviews',
            path: '/review-management',
            icon: <QuizIcon />
        },

        viewMetrics: {
            title: 'Metrics',
            description: 'View and manage metrics',
            path: '/view-metrics',
            icon: <InsightsIcon />
        },
        scheduledJobs: {
            title: 'Scheduled Jobs',
            description: 'View and manage automated review schedules and job execution status',
            path: '/scheduled-jobs',
            icon: <AccessTimeIcon />
        },
        reports: {
            title: 'Reports',
            description: 'View analytics and reports on review entities, metrics, and trends',
            path: '/reports',
            icon: <BarChartIcon />
        },
        dashboard: {
            title: 'Executive Dashboard',
            description: 'View hierarchical dashboards with demographics, metrics, and review status across your organization',
            path: '/dashboard',
            icon: <DashboardIcon />
        }
    };

    const getTilesByRoles = () => {
        const {
            userManagement,
            reviewEntityManagement,
            organizationSetup,
            reviewManagement,
            viewMetrics,
            scheduledJobs,
            reports,
            dashboard
        } = allTiles;

        const tiles = [viewMetrics, dashboard];

        if (UserUtils.isAdmin(user)) {
            tiles.push(
                userManagement,
                reviewEntityManagement,
                organizationSetup,
                scheduledJobs
            );
        } else if (UserUtils.canAccessReviewManagement(user)) {
            // REVIEWER, REVIEWEE, and DATA_ENTRY_SUPPORT share Review Management access
            tiles.push(reviewManagement);
        }

        // Add Reports as the last tile for all users
        tiles.push(reports);

        return tiles;
    };

    const tiles = getTilesByRoles();

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <TitleBar />
            <Container maxWidth="xl" sx={{ pt: 6, pb: 8 }}>
                <Box sx={{ mb: 6 }}>
                    <Typography 
                        variant="h3" 
                        sx={{ 
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 2,
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}
                    >
                        Statusense Console
                    </Typography>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            color: 'text.secondary',
                            fontWeight: 400,
                            maxWidth: 600
                        }}
                    >
                        Welcome back, {user?.display_name || user?.user_name}. 
                        Choose from the available modules below to get started.
                    </Typography>
                </Box>
                
                <Grid container spacing={4}>
                    {tiles.map((tile, index) => (
                        <HomeTile
                            key={index}
                            title={tile.title}
                            description={tile.description}
                            onClick={() => navigate(tile.path)}
                            icon={tile.icon}
                        />
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default Home;
